import { useSelector } from "react-redux";
import { Outlet, useLocation, Navigate, useNavigate } from "react-router";
import { enqueueSnackbar } from "notistack";
import Header from "../core/common/header";
import Sidebar from "../core/common/sidebar";
import ThemeSettings from "../core/common/theme-settings";
import Loader from "../core/common/loader";
import { useEffect, useState, useMemo, memo } from "react";
import { all_routes } from "./router/all_routes";
import { StorageProvider } from "../core/utils/storageContext";

const SESSION_TIMEOUT_MS = 1.5 * 60 * 60 * 1000; // 1.5 hours

const Feature = memo(() => {
  const routes = all_routes;
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(true);
  const user = useSelector((state: any) => state.userSlice.user);
  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  );
  const miniSidebar = useSelector(
    (state: any) => state.sidebarSlice.miniSidebar
  );
  const expandMenu = useSelector((state: any) => state.sidebarSlice.expandMenu);

  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);
  const dataTopBar = useSelector((state: any) => state.themeSetting.dataTopBar);
  const dataTheme = useSelector((state: any) => state.themeSetting.dataTheme);
  const dataSidebar = useSelector(
    (state: any) => state.themeSetting.dataSidebar
  );
  const dataSidebarBg = useSelector(
    (state: any) => state.themeSetting.dataSidebarBg
  );
  const dataColor = useSelector((state: any) => state.themeSetting.dataColor);
  const location = useLocation();

  // Memoize className computation to avoid recalculation on every render
  const classNameString = useMemo(() => {
    return `
       ${dataLayout === "mini_layout" ? "mini-sidebar" : ""}
      ${miniSidebar && dataLayout !== "mini_layout" ? "mini-sidebar" : ""}
     ${(expandMenu && miniSidebar) ||
          (expandMenu && dataLayout === "mini_layout")
          ? "expand-menu"
          : ""
        }
      ${dataLayout === "default_layout" ? "default-layout" : ""}
      ${dataLayout === "boxed_layout" ? "layout-box-mode" : ""}

      ${dataTheme === "dark_data_theme" ? "dark-data-theme" : ""}
      ${dataLayout === "dark_data_theme" ? "dark-data-theme" : ""}
      ${dataLayout === "rtl" ? "layout-mode-rtl" : ""}

      ${dataTopBar === "default_topbar_color" ? "default-topbar" : ""}
      ${dataTopBar === "dark_topbar_color" ? "dark-topbar" : ""}
      ${dataTopBar === "primary_topbar_color" ? "primary-topbar" : ""}
      ${dataTopBar === "grey_topbar_color" ? "grey-topbar" : ""}
      ${dataTheme === "default_data_theme" ? "default-data-theme" : ""}
      
      ${dataSidebar === "default_data_sidebar" ? "default-data-sidebar" : ""}
      ${dataSidebar === "dark_data_sidebar" ? "dark-data-sidebar" : ""}
      ${dataSidebar === "primary_data_sidebar" ? "primary-data-sidebar" : ""}
      ${dataSidebar === "darkblack_data_sidebar" ? "darkblack-data-sidebar" : ""
        }
      ${dataSidebar === "darkblue_data_sidebar" ? "darkblue-data-sidebar" : ""}
      ${dataSidebarBg === "default_data_sidebar_bg"
          ? "default-data-sidebar-bg"
          : ""
        }
      ${dataSidebarBg === "data_sidebar_1" ? "data-sidebar-1" : ""}
      ${dataSidebarBg === "data_sidebar_2" ? "data-sidebar-2" : ""}
      ${dataSidebarBg === "data_sidebar_3" ? "data-sidebar-3" : ""}
      ${dataSidebarBg === "data_sidebar_4" ? "data-sidebar-4" : ""}
      ${dataSidebarBg === "data_sidebar_5" ? "data-sidebar-5" : ""}
      ${dataSidebarBg === "data_sidebar_6" ? "data-sidebar-6" : ""}
      ${dataColor === "default_data_color" ? "default-data-color" : ""}
      ${dataColor === "violet_data_color" ? "violet-data-color" : ""}
      ${dataColor === "pink_data_color" ? "pink-data-color" : ""}
      ${dataColor === "orange_data_color" ? "orange-data-color" : ""}
      ${dataColor === "green_data_color" ? "green-data-color" : ""}
      ${dataColor === "red_data_color" ? "red-data-color" : ""}
      `.trim();
  }, [dataLayout, miniSidebar, expandMenu, dataTheme, dataTopBar, dataSidebar, dataSidebarBg, dataColor]);
  useEffect(() => {
    if (dataTheme === "dark_data_theme") {
      document.documentElement.setAttribute("data-theme", "darks");
    } else {
      document.documentElement.setAttribute("data-theme", "");
    }
  }, [dataTheme]);
  useEffect(() => {
    if (
      location.pathname === routes.adminDashboard ||
      location.pathname === routes.teacherDashboard ||
      location.pathname === routes.studentDashboard ||
      location.pathname === routes.parentDashboard
    ) {
      // Show the loader when navigating to a new route
      setShowLoader(true);

      // Hide the loader after 2 seconds
      const timeoutId = setTimeout(() => {
        setShowLoader(false);
      }, 2000);

      return () => {
        clearTimeout(timeoutId); // Clear the timeout when component unmounts
      };
    } else {
      setShowLoader(false);
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Auto logout after 1.5 hours of session
  useEffect(() => {
    const checkSessionTimeout = () => {
      const role = localStorage.getItem("role");
      const loginTimestamp = localStorage.getItem("login_timestamp");

      if (!role || !loginTimestamp) return;

      const elapsed = Date.now() - parseInt(loginTimestamp, 10);
      if (elapsed >= SESSION_TIMEOUT_MS) {
        localStorage.removeItem("role");
        localStorage.removeItem("user_id");
        localStorage.removeItem("login_timestamp");
        enqueueSnackbar("Session expired. Please log in again.", { variant: "info" });
        const loginRoute =
          role === "admin"
            ? routes.login3
            : role === "teacher"
              ? routes.login
              : routes.login2;
        navigate(loginRoute, { replace: true });
      }
    };

    checkSessionTimeout();
    const intervalId = setInterval(checkSessionTimeout, 60000); // Check every minute
    return () => clearInterval(intervalId);
  }, [navigate]);

  // Memoize redirect check
  const shouldRedirectToDueFees = useMemo(() => {
    return user &&
      user.role === "student" &&
      user.fees_status !== "Paid" &&
      (location.pathname !== "/due-fees" && location.pathname !== "/checkout" && location.pathname !== "/payment-success");
  }, [user, location.pathname]);

  // If user should be redirected to due-fees, redirect immediately
  if (shouldRedirectToDueFees) {
    return <Navigate to="/due-fees" replace />;
  }

  const Preloader = () => {
    return (
      <div id="global-loader">
        <div className="page-loader"></div>
      </div>
    );
  };
  return (
    <StorageProvider>
      <div
        className={classNameString}
      >
        {showLoader ? (
          <>
            <Preloader />
            <div
              className={`main-wrapper 
          ${mobileSidebar ? "slide-nav" : ""}`}
            >
              <Header />
              <Sidebar />
              <main role="main" id="main-content">
                <Outlet />
              </main>
            </div>
          </>
        ) : (
          <>
            <div
              className={`main-wrapper 
          ${mobileSidebar ? "slide-nav" : ""}`}
            >
              <Header />
              <Sidebar />
              <main role="main" id="main-content">
                <Outlet />
              </main>
            </div>
          </>
        )}
        {/* <Loader/> */}

        <div className="sidebar-overlay"></div>
      </div>
    </StorageProvider>
  );
});

Feature.displayName = 'Feature';

export default Feature;
